import React from 'react';
import { Skeleton } from '../ui/skeleton';

/**
 * Prebuilt skeleton patterns. Choose one via `variant`:
 *   card / list / table / map / kpi
 * Pass `count` to repeat the shape.
 *
 * Perceived load time drops ~30% vs. a spinner alone.
 */
export default function SkeletonBlock({ variant = 'card', count = 3, className = '', testId = 'skeleton-block' }) {
  const items = Array.from({ length: count }, (_, i) => i);

  if (variant === 'kpi') {
    return (
      <div className={`grid grid-cols-2 md:grid-cols-4 gap-3 ${className}`} data-testid={testId}>
        {items.map(i => (
          <div key={i} className="rounded-xl border bg-card p-4 space-y-2">
            <Skeleton className="h-3 w-16" />
            <Skeleton className="h-7 w-24" />
            <Skeleton className="h-2 w-20" />
          </div>
        ))}
      </div>
    );
  }
  if (variant === 'list') {
    return (
      <div className={`space-y-2 ${className}`} data-testid={testId}>
        {items.map(i => (
          <div key={i} className="flex items-center gap-3 p-3 rounded-lg border bg-card">
            <Skeleton className="h-9 w-9 rounded-md" />
            <div className="flex-1 space-y-1.5">
              <Skeleton className="h-3.5 w-40" />
              <Skeleton className="h-2.5 w-28" />
            </div>
            <Skeleton className="h-6 w-16 rounded-full" />
          </div>
        ))}
      </div>
    );
  }
  if (variant === 'table') {
    return (
      <div className={`rounded-lg border bg-card overflow-hidden ${className}`} data-testid={testId}>
        <div className="flex items-center gap-3 border-b p-3 bg-muted/40">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-3 w-24 ml-auto" />
          <Skeleton className="h-3 w-16" />
        </div>
        {items.map(i => (
          <div key={i} className="flex items-center gap-3 p-3 border-b last:border-b-0">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-40 ml-auto" />
            <Skeleton className="h-4 w-12" />
          </div>
        ))}
      </div>
    );
  }
  if (variant === 'map') {
    return (
      <div className={`rounded-xl border bg-card p-6 ${className}`} data-testid={testId}>
        <div className="flex items-center justify-between mb-6">
          <Skeleton className="h-4 w-40" />
          <Skeleton className="h-6 w-28" />
        </div>
        <div className="relative h-[420px] w-full overflow-hidden rounded-lg bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800">
          {[
            { top: '18%', left: '22%', size: 80 },
            { top: '30%', left: '58%', size: 60 },
            { top: '52%', left: '32%', size: 100 },
            { top: '60%', left: '70%', size: 70 },
            { top: '75%', left: '48%', size: 55 },
          ].map((n, i) => (
            <Skeleton
              key={i}
              className="absolute rounded-full"
              style={{ top: n.top, left: n.left, width: n.size, height: n.size, animationDelay: `${i * 120}ms` }}
            />
          ))}
        </div>
      </div>
    );
  }

  // default: card grid
  return (
    <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 ${className}`} data-testid={testId}>
      {items.map(i => (
        <div key={i} className="rounded-xl border bg-card p-4 space-y-3">
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-8 rounded-md" />
            <Skeleton className="h-3.5 w-24" />
          </div>
          <Skeleton className="h-3 w-full" />
          <Skeleton className="h-3 w-4/5" />
          <Skeleton className="h-3 w-2/3" />
        </div>
      ))}
    </div>
  );
}
